/**
 * Kiwi Size Chart injection anchor.
 *
 * The "Size" option UI on the PDP is rendered client-side by the Glo Color
 * Swatch (Globo) app, producing markup like:
 *   <legend class="name-option" data-name="Size">Size<span class="name-variant">...</span></legend>
 *
 * Because this markup is injected asynchronously by a third-party app, we
 * can't add a stable Liquid anchor next to it. This script watches for the
 * Size legend and inserts an empty `<div class="ks-inject-point"></div>`
 * immediately after it, giving the Kiwi Sizing app a stable CSS selector
 * (`.ks-inject-point`) to target via its Injection Selector setting.
 */
(function () {
  'use strict';

  const SIZE_LEGEND_SELECTOR = '.name-option[data-name="Size" i]';

  /**
   * Inserts a `.ks-inject-point` div immediately after the given legend,
   * unless one is already present as the next sibling.
   * @param {Element} legend - The `.name-option[data-name="Size"]` element.
   */
  function injectKsPoint(legend) {
    if (!legend || legend.querySelector(':scope > .ks-inject-point')) return;

    const injectPoint = document.createElement('div');
    injectPoint.className = 'ks-inject-point';
    legend.insertAdjacentElement('beforeend', injectPoint);
  }

  /**
   * Finds and processes any Size legends within a given root element.
   * @param {Element} root - Element to search within.
   */
  function processSizeLegends(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(SIZE_LEGEND_SELECTOR).forEach(injectKsPoint);
  }

  // Process any Size legends already in the DOM when this script runs.
  processSizeLegends(document);

  // Watch for Globo's async render and any later re-renders (e.g. after a
  // variant change), which may remove and recreate the legend + our div.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        // The node itself may be the legend...
        if (node.matches?.(SIZE_LEGEND_SELECTOR)) {
          injectKsPoint(node);
        }

        // ...or it may be a subtree containing one or more legends.
        processSizeLegends(node);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
